import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs';

import { DatadogRumService, FullStoryService } from '@ninety/mobile/platform/services';

import { AnalyticsSessionSelectors } from './analytics-session.selectors';

const RUM_LINK_EVENT = 'Datadog RUM Session';

/**
 * Bridges the two session recorders so an investigation can start in either and reach the other.
 *
 * This belongs to neither vendor, which is why it is not in `DatadogEffects` or `FullStoryEffects`.
 * Both recorders announce their session as an action, so the wait for them is expressed as state
 * arriving rather than as flags and timers here.
 */
@Injectable()
export class SessionCrossLinkEffects {
  private readonly store = inject(Store);
  private readonly rum = inject(DatadogRumService);
  private readonly fullStory = inject(FullStoryService);

  /**
   * Fires once both recorders have a session, and again whenever either starts a new one:
   * `linkableSessions` is memoized, so it re-emits exactly when one of them changes and a rolled
   * FullStory session is not left without its Datadog link.
   *
   * Waiting for a FullStory session URL, rather than only for the interface, also means the link
   * event is never sent into a session that does not exist yet.
   */
  crossLinkSessions$ = createEffect(
    () =>
      this.store.select(AnalyticsSessionSelectors.linkableSessions).pipe(
        filter(
          (sessions): sessions is { rumSessionId: string; fullStorySessionUrl: string } =>
            !!sessions.rumSessionId && !!sessions.fullStorySessionUrl
        ),
        tap(({ rumSessionId, fullStorySessionUrl }) => {
          this.rum.setGlobalContextProperty('fullstory_session_url', fullStorySessionUrl);
          void this.fullStory.getSessionIdAsync().then(id => {
            if (id) this.rum.setGlobalContextProperty('fullstory_session_id', id);
          });

          // Recorded as a session-scoped custom event rather than a page property: FullStory
          // documents its Pages API as unsupported inside WebViews. A custom event also stays with
          // the session it belongs to, so an older session keeps its own Datadog link.
          this.fullStory.trackEvent(RUM_LINK_EVENT, {
            dd_rum_session_id: rumSessionId,
            dd_rum_session_url: this.buildRumSessionUrl(rumSessionId),
          });
        })
      ),
    { dispatch: false }
  );

  private buildRumSessionUrl(sessionId: string): string {
    return `https://app.datadoghq.com/rum/replay/sessions/${sessionId}`;
  }
}
